<!-- 微信二维码授权信息组件 -->
<template>
  <div
    id="wechat-login-container"
    :style="{
      width: props.size,
      height: props.size,
    }"
    :class="[' flex items-center justify-center'].join('')"
  ></div>
</template>

<script setup lang="ts">
  import { onMounted, onUnmounted } from 'vue'
  import { v4 as uuidv4 } from 'uuid'
  import { toast } from 'vue-sonner'

  import { useLocaleStore } from '@/stores'
  import { EN_US_LOCALE } from '@/utils/locale'
  import { getAssetBaseUrl } from '@/utils/oss'
  const { t, language } = useLocaleStore()

  const emit = defineEmits(['scan-login-success'])
  const props = defineProps({
    redirectUrl: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: '11.32rem',
    },
    handleIframeMessage: { type: Function, default: null },
  })

  function loadWXSDK() {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      // 微信登录 SDK 挂在静态资源 CDN；按渠道走 getAssetBaseUrl（原 VITE_ASSEST_CDN 已废弃）
      script.src = `${getAssetBaseUrl()}/utils/wx/wxLogin.js`
      script.onload = resolve
      script.onerror = () => reject(new Error('微信SDK加载失败'))
      document.head.appendChild(script)
    })
  }

  // 监听来自 iframe 的消息
  const handleMessage = (event: MessageEvent) => {
    // 已在子页面限制origin，这里不做验证origin验证
    if (!event.data) return
    // WxLogin 会发送很多消息，我们只关心我们自己页面重定向后发出的消息
    // 假设我们的重定向页面返回的是 JSON 字符串
    try {
      const data = JSON.parse(event.data)
      // 简单判断是否是期望的格式，避免被微信其他消息误触发
      if (data && (data.code || data.data || data.errorMes)) {
        props.handleIframeMessage?.(event)
      }
    } catch (e) {
      // 忽略无法解析的消息
    }
  }

  async function init() {
    try {
      await loadWXSDK()
      new WxLogin({
        self_redirect: true, // true: iframe跳转; false: 页面跳转
        id: 'wechat-login-container',
        appid: 'wxf4271a14afdc34eb',
        scope: 'snsapi_login',
        // 注意：如果是 self_redirect: true，这个 redirect_uri 返回的页面
        // 必须包含一段 JS 代码来通过 window.parent.postMessage 发送 code
        redirect_uri: encodeURIComponent(props.redirectUrl),
        state: uuidv4(),
        style: 'black',
        href: '',
        fast_login: 1,
        stylelite: 1,
        lang: language === EN_US_LOCALE ? 'en' : '',
      })
    } catch (error) {
      console.error(error)
      toast.error(t('toast.wechatLoginFail'))
    }
  }

  onMounted(() => {
    init()
    window.addEventListener('message', handleMessage)
  })

  onUnmounted(() => {
    window.removeEventListener('message', handleMessage)
  })
</script>

<style lang="scss" scoped>
  /* 使用 :deep() 穿透 scoped 限制，影响动态生成的 iframe 标签本身 */
  #wechat-login-container {
    :deep(iframe) {
      width: 100% !important;
      height: 100% !important;
    }
  }
</style>
